export default class OrganicHexagonReveal {
    constructor(canvasId, imageUrl, options = {}) {
        this.canvas = document.getElementById(canvasId);
        if (!this.canvas) {
            console.error(`Canvas element with ID "${canvasId}" not found`);
            return;
        }

        this.ctx = this.canvas.getContext('2d');

        // Background image to reveal
        this.backgroundImage = new Image();
        this.backgroundImage.src = imageUrl;
        this.imageLoaded = false;
        this.backgroundImage.onload = () => {
            this.imageLoaded = true;
            this.calculateImageScaling();
            // Generate pattern once image is loaded
            this.createDenseHexagonPattern();
            // Start intro animation
            this.startIntroAnimation();
        };

        // Configuration options with defaults
        this.config = {
            // Hexagon properties
            hexSize: options.hexSize || 36,
            horizontalSpacingFactor: options.horizontalSpacingFactor || 1.12,
            verticalSpacingFactor: options.verticalSpacingFactor || 0.85,

            // Pattern settings (simplified)
            patternDensity: options.patternDensity || 0.45,            // Percentage of hexagons to activate
            rightAlignmentStrength: options.rightAlignmentStrength || 0.8,  // How strongly to align to the right (0-1)
            edgeBuffer: options.edgeBuffer || 0.06,                   // Small buffer from edges (percentage of screen)

            // Opacity settings
            baseOpacity: options.baseOpacity !== undefined ? options.baseOpacity : 0.0,     // Base visibility
            hoverOpacity: options.hoverOpacity !== undefined ? options.hoverOpacity : 1.0,  // Hover visibility
            nearOpacity: options.nearOpacity !== undefined ? options.nearOpacity : 0.6,     // Near visibility
            farOpacity: options.farOpacity !== undefined ? options.farOpacity : 0.3,        // Far visibility

            // Opacity settings
            baseOpacity: options.baseOpacity !== undefined ? options.baseOpacity : 0.0,     // Base visibility
            hoverOpacity: options.hoverOpacity !== undefined ? options.hoverOpacity : 1.0,  // Hover visibility
            nearOpacity: options.nearOpacity !== undefined ? options.nearOpacity : 0.6,     // Near visibility
            farOpacity: options.farOpacity !== undefined ? options.farOpacity : 0.3,        // Far visibility

            // Transition speeds
            slowFadeSpeed: options.slowFadeSpeed || 0.03,
            activeTransitionSpeed: options.activeTransitionSpeed || 0.1,
            proximityThresholds: options.proximityThresholds || [1, 1.5, 2.5],
            minActivityFactor: options.minActivityFactor || 0.2,

            // Background color
            backgroundColor: options.backgroundColor || '#121212',

            // Border configuration
            borderWidth: options.borderWidth || 1.5,
            borderBaseOpacity: options.borderBaseOpacity || 0.08,
            borderMaxOpacity: options.borderMaxOpacity || 0.9,
            borderGradientRange: options.borderGradientRange || 6,

            // Intro animation
            introFlashDuration: options.introFlashDuration || 400,    // Duration of the flash in ms
            introFlashDelay: options.introFlashDelay || 30,           // Delay between hexagon flashes
            introFlashMaxOpacity: options.introFlashMaxOpacity || 1.0, // Max opacity during flash

            // Image fitting
            imageFit: options.imageFit || 'cover',

            // Performance
            cullingMargin: options.cullingMargin || 50,
            throttleTime: options.throttleTime || 16,
            optimizationLevel: options.optimizationLevel || 'auto'
        };

        // Apply automatic optimization
        if (this.config.optimizationLevel === 'auto') {
            if (this.config.hexSize <= 20) {
                this.config.throttleTime = 32;
                this.config.cullingMargin = 30;
            } else if (this.config.hexSize <= 30) {
                this.config.throttleTime = 24;
                this.config.cullingMargin = 40;
            }
        }

        // Calculate derived measurements
        this.measurements = this.calculateMeasurements();

        // Mouse tracking state
        this.mouse = {
            x: 0,
            y: 0,
            prevX: 0,
            prevY: 0,
            speed: 0,
            lastMoveTime: 0,
            lastProcessedTime: 0
        };

        // Animation state
        this.animationState = {
            introActive: false,
            introProgress: 0,
            flashingHexagons: []
        };

        this.allHexagons = [];     // All possible hexagon positions
        this.activeHexagons = [];  // Only the hexagons that are part of the pattern
        this.visibleHexagons = []; // Active hexagons in viewport

        this.resizeCanvas();
        this.setupEventListeners();

        this.animate = this.animate.bind(this);
        requestAnimationFrame(this.animate);
    }

    // Calculate image scaling to maintain proper aspect ratio
    calculateImageScaling() {
        if (!this.imageLoaded) return;

        const imgWidth = this.backgroundImage.width;
        const imgHeight = this.backgroundImage.height;
        const canvasWidth = this.canvas.width;
        const canvasHeight = this.canvas.height;

        // Calculate scaling and positioning based on fit mode
        if (this.config.imageFit === 'cover') {
            const scale = Math.max(canvasWidth / imgWidth, canvasHeight / imgHeight);
            this.imgScale = {
                width: imgWidth * scale,
                height: imgHeight * scale,
                x: (canvasWidth - imgWidth * scale) / 2,
                y: (canvasHeight - imgHeight * scale) / 2
            };
        } else if (this.config.imageFit === 'contain') {
            const scale = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);
            this.imgScale = {
                width: imgWidth * scale,
                height: imgHeight * scale,
                x: (canvasWidth - imgWidth * scale) / 2,
                y: (canvasHeight - imgHeight * scale) / 2
            };
        } else {
            this.imgScale = {
                width: canvasWidth,
                height: canvasHeight,
                x: 0,
                y: 0
            };
        }
    }

    // Calculate measurements based on hexagon size
    calculateMeasurements() {
        const { hexSize, horizontalSpacingFactor, verticalSpacingFactor } = this.config;

        return {
            hexWidth: hexSize * Math.sqrt(3),
            hexHeight: hexSize * 2,
            horizontalSpacing: hexSize * Math.sqrt(3) * horizontalSpacingFactor,
            verticalSpacing: hexSize * 2 * verticalSpacingFactor
        };
    }

    resizeCanvas() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;

        // Calculate the dimensions for the grid
        const { edgeBuffer } = this.config;

        // Create buffer around edges
        const bufferSize = Math.min(this.canvas.width, this.canvas.height) * edgeBuffer;

        this.gridBounds = {
            left: bufferSize,
            right: this.canvas.width - bufferSize,
            top: bufferSize,
            bottom: this.canvas.height - bufferSize
        };

        // Update calculated values
        this.viewportDiagonal = Math.sqrt(
            this.canvas.width * this.canvas.width +
            this.canvas.height * this.canvas.height
        );

        // Recalculate image scaling
        this.calculateImageScaling();

        // Regenerate the hexagon grid and pattern
        this.createHexagonGrid();

        if (this.imageLoaded) {
            this.createDenseHexagonPattern();
        }
    }

    createHexagonGrid() {
        this.allHexagons = [];

        const { horizontalSpacing, verticalSpacing } = this.measurements;
        const { baseOpacity, borderBaseOpacity } = this.config;
        const { left, right, top, bottom } = this.gridBounds;

        // Calculate available space
        const availableWidth = right - left;
        const availableHeight = bottom - top;

        // Calculate grid dimensions with extra rows/cols to ensure coverage
        const numCols = Math.ceil(availableWidth / horizontalSpacing) + 2;
        const numRows = Math.ceil(availableHeight / verticalSpacing) + 2;

        // Precalculate half spacing for staggered rows
        const halfSpacing = horizontalSpacing / 2;

        for (let row = 0; row < numRows; row++) {
            for (let col = 0; col < numCols; col++) {
                const offset = row % 2 === 1 ? halfSpacing : 0;

                const x = left + col * horizontalSpacing + offset;
                const y = top + row * verticalSpacing;

                this.allHexagons.push({
                    x,
                    y,
                    row,
                    col,
                    active: false,
                    currentOpacity: 0,
                    targetOpacity: 0,
                    borderOpacity: borderBaseOpacity,
                    borderGradientAngle: 0,
                    borderGradientStrength: 0,
                    // Animation properties
                    flashProgress: 0,
                    flashActive: false,
                    flashDelay: 0,
                    // Optimization properties
                    lastDistanceToMouse: Infinity,
                    cachedPoints: null,
                    needsRedraw: true
                });
            }
        }
    }

    // Create a dense right-aligned hexagon pattern
    createDenseHexagonPattern() {
        if (!this.allHexagons || this.allHexagons.length === 0) return;

        // Reset all hexagons to inactive
        this.allHexagons.forEach(hex => {
            hex.active = false;
            hex.targetOpacity = 0;
            hex.currentOpacity = 0;
        });

        const { patternDensity, rightAlignmentStrength } = this.config;
        const maxCol = this.getMaxCol();
        const maxRow = this.getMaxRow();

        // Determine target count of active hexagons
        const totalHexagons = this.allHexagons.length;
        const targetActiveCount = Math.floor(totalHexagons * patternDensity);

        // Sort hexagons by position for selection
        // The higher the rightAlignmentStrength, the more we favor the right side
        const sortedHexagons = [...this.allHexagons].sort((a, b) => {
            // Normalize column position (0 = left, 1 = right)
            const aColNorm = a.col / maxCol;
            const bColNorm = b.col / maxCol;

            // Create a weighted score that favors the right side
            // The formula below gives higher scores to hexagons on the right
            // Higher rightAlignmentStrength means stronger right bias
            const aScore = aColNorm * rightAlignmentStrength + Math.random() * (1 - rightAlignmentStrength);
            const bScore = bColNorm * rightAlignmentStrength + Math.random() * (1 - rightAlignmentStrength);

            // Sort by score (higher score = more likely to be selected)
            return bScore - aScore;
        });

        // Take the top hexagons based on the density setting
        const selectedHexagons = sortedHexagons.slice(0, targetActiveCount);

        // Activate the selected hexagons and assign delay values for intro animation
        selectedHexagons.forEach((hex, index) => {
            hex.active = true;

            // Calculate a sequential delay based on position
            // With higher indices for right-side hexagons, they'll animate first
            const rightPosition = hex.col / maxCol; // 0 = left, 1 = right
            const animationOrder = (1 - rightPosition) * selectedHexagons.length;
            hex.flashActive = true;
            hex.flashDelay = animationOrder * 5 + Math.random() * 20; // Some randomness in delay
        });

        // Create the active hexagons array
        this.activeHexagons = this.allHexagons.filter(hex => hex.active);

        // Update visible hexagons
        this.updateVisibleHexagons();

        console.log(`Created dense pattern with ${this.activeHexagons.length} active hexagons (${Math.round(this.activeHexagons.length / totalHexagons * 100)}% of total)`);
    }

    // Get the maximum row index
    getMaxRow() {
        let maxRow = 0;
        this.allHexagons.forEach(hex => {
            if (hex.row > maxRow) maxRow = hex.row;
        });
        return maxRow;
    }

    // Get the maximum column index
    getMaxCol() {
        let maxCol = 0;
        this.allHexagons.forEach(hex => {
            if (hex.col > maxCol) maxCol = hex.col;
        });
        return maxCol;
    }

    // Get hexagon at the specified row and column
    getHexagonAt(row, col) {
        return this.allHexagons.find(hex => hex.row === row && hex.col === col);
    }

    // Get neighbors of a hexagon
    getNeighbors(hexagon) {
        const { row, col } = hexagon;
        const isOddRow = row % 2 === 1;

        // Neighbor directions depend on whether the row is odd or even
        const directions = [
            { r: -1, c: 0 },          // Top
            { r: -1, c: isOddRow ? 1 : -1 }, // Top-right/left
            { r: 0, c: 1 },           // Right
            { r: 1, c: isOddRow ? 1 : -1 },  // Bottom-right/left
            { r: 1, c: 0 },           // Bottom
            { r: 0, c: -1 }           // Left
        ];

        const neighbors = [];

        for (const dir of directions) {
            const neighborRow = row + dir.r;
            const neighborCol = col + dir.c;

            const neighbor = this.getHexagonAt(neighborRow, neighborCol);
            if (neighbor) {
                neighbors.push(neighbor);
            }
        }

        return neighbors;
    }

    // Start the intro animation sequence
    startIntroAnimation() {
        this.animationState.introActive = true;
        this.animationState.introProgress = 0;

        // Sort active hexagons by their root depth to animate from root outward
        const sortedHexagons = [...this.activeHexagons].sort((a, b) => a.rootDepth - b.rootDepth);

        // Calculate delays based on depth - deeper hexagons animate later
        const { introFlashDelay } = this.config;
        sortedHexagons.forEach((hex, index) => {
            hex.flashActive = true;
            hex.flashProgress = 0;
            hex.flashDelay = hex.rootDepth * introFlashDelay + Math.random() * introFlashDelay * 2;
            this.animationState.flashingHexagons.push(hex);
        });
    }

    // Get points of a hexagon for drawing
    getHexagonPoints(x, y) {
        const { hexSize } = this.config;
        const points = [];

        // Pre-calculate values
        const anglePi6 = Math.PI / 6;
        const sinValues = [];
        const cosValues = [];

        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3 + anglePi6;
            cosValues[i] = Math.cos(angle);
            sinValues[i] = Math.sin(angle);
        }

        for (let i = 0; i < 6; i++) {
            const px = x + hexSize * cosValues[i];
            const py = y + hexSize * sinValues[i];
            points.push({ x: px, y: py });
        }

        return points;
    }

    // Draw a single hexagon that reveals the image
    drawHexagon(hexagon) {
        if (!this.imageLoaded || !hexagon.active) return;

        const { x, y, currentOpacity, borderOpacity, borderGradientAngle, borderGradientStrength } = hexagon;
        const { borderWidth, backgroundColor } = this.config;
        const ctx = this.ctx;

        // Cache the hexagon points
        if (!hexagon.cachedPoints) {
            hexagon.cachedPoints = this.getHexagonPoints(x, y);
        }
        const points = hexagon.cachedPoints;

        // Calculate flash opacity if active
        let displayOpacity = currentOpacity;
        if (hexagon.flashActive && this.animationState.introActive) {
            // Add flash opacity to current opacity
            const introOpacity = this.calculateFlashOpacity(hexagon);
            displayOpacity = Math.max(currentOpacity, introOpacity);
        }

        // Skip rendering if opacity is extremely low
        if (displayOpacity < 0.001 && borderOpacity < 0.01 && !borderGradientStrength) {
            return;
        }

        // Create clipping path for the hexagon
        ctx.save();
        ctx.beginPath();
        points.forEach((point, i) => {
            if (i === 0) {
                ctx.moveTo(point.x, point.y);
            } else {
                ctx.lineTo(point.x, point.y);
            }
        });
        ctx.closePath();
        ctx.clip();

        // Draw the image portion inside the hexagon with opacity
        ctx.globalAlpha = displayOpacity;
        ctx.drawImage(
            this.backgroundImage,
            this.imgScale.x, this.imgScale.y,
            this.imgScale.width, this.imgScale.height
        );
        ctx.globalAlpha = 1.0;

        ctx.restore();

        // Draw border
        if (borderOpacity > 0.01 || borderGradientStrength > 0.01) {
            ctx.beginPath();
            points.forEach((point, i) => {
                if (i === 0) {
                    ctx.moveTo(point.x, point.y);
                } else {
                    ctx.lineTo(point.x, point.y);
                }
            });
            ctx.closePath();

            // Default border style
            ctx.strokeStyle = `rgba(255, 255, 255, ${borderOpacity})`;

            // Apply gradient if there's movement
            if (borderGradientStrength > 0.01) {
                const gradient = ctx.createLinearGradient(
                    x + Math.cos(borderGradientAngle) * this.config.hexSize,
                    y + Math.sin(borderGradientAngle) * this.config.hexSize,
                    x - Math.cos(borderGradientAngle) * this.config.hexSize,
                    y - Math.sin(borderGradientAngle) * this.config.hexSize
                );

                // Define gradient stops
                const startOpacity = borderOpacity * borderGradientStrength * 1.5;
                const midOpacity = borderOpacity * borderGradientStrength * 0.8;
                const endOpacity = this.config.borderBaseOpacity;

                gradient.addColorStop(0, `rgba(255, 255, 255, ${startOpacity})`);
                gradient.addColorStop(0.5, `rgba(255, 255, 255, ${midOpacity})`);
                gradient.addColorStop(1, `rgba(255, 255, 255, ${endOpacity})`);

                ctx.strokeStyle = gradient;
            }

            ctx.lineWidth = borderWidth;
            ctx.stroke();
        }
    }

    // Calculate flash opacity based on animation progress
    calculateFlashOpacity(hexagon) {
        if (!hexagon.flashActive) return 0;

        const { introFlashDuration, introFlashMaxOpacity } = this.config;
        const now = performance.now();

        // Wait for delay before starting animation
        if (this.animationState.introStartTime + hexagon.flashDelay > now) {
            return 0;
        }

        // Calculate progress (0 to 1)
        const elapsed = now - (this.animationState.introStartTime + hexagon.flashDelay);
        const progress = Math.min(1, elapsed / introFlashDuration);

        // Simple bell curve - rises then falls
        // Peak at 0.5 progress
        if (progress < 0.5) {
            // Rising phase (0 to 0.5 -> 0 to 1)
            return introFlashMaxOpacity * (progress * 2);
        } else {
            // Falling phase (0.5 to 1 -> 1 to 0)
            return introFlashMaxOpacity * (1 - (progress - 0.5) * 2);
        }
    }

    // Calculate distance between two points
    distance(x1, y1, x2, y2) {
        return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    }

    // Calculate angle between two points
    angle(x1, y1, x2, y2) {
        return Math.atan2(y2 - y1, x2 - x1);
    }

    updateMouseActivity(timestamp) {
        const { x, y, prevX, prevY, lastMoveTime } = this.mouse;
        const timeSinceLastMove = timestamp - lastMoveTime;

        // Calculate current speed
        const dx = x - prevX;
        const dy = y - prevY;
        let speed = Math.sqrt(dx * dx + dy * dy);

        // Decay speed if mouse hasn't moved recently
        if (timeSinceLastMove > 100) {
            speed *= Math.max(0, 1 - (timeSinceLastMove - 100) / 500);
        }

        this.mouse.speed = speed;
        this.mouse.prevX = x;
        this.mouse.prevY = y;
    }

    // Filter hexagons to include only active ones visible in viewport
    updateVisibleHexagons() {
        const { cullingMargin } = this.config;

        this.visibleHexagons = this.activeHexagons.filter(hexagon => {
            return (
                hexagon.x >= -cullingMargin &&
                hexagon.x <= this.canvas.width + cullingMargin &&
                hexagon.y >= -cullingMargin &&
                hexagon.y <= this.canvas.height + cullingMargin
            );
        });
    }

    // Update opacities based on mouse position
    updateOpacities(timestamp) {
        // Only update at specified intervals (throttling)
        if (timestamp - this.mouse.lastProcessedTime < this.config.throttleTime) {
            return;
        }
        this.mouse.lastProcessedTime = timestamp;

        const {
            hexSize,
            baseOpacity,
            hoverOpacity,
            nearOpacity,
            farOpacity,
            slowFadeSpeed,
            activeTransitionSpeed,
            proximityThresholds,
            minActivityFactor,
            borderMaxOpacity,
            borderBaseOpacity,
            borderGradientRange
        } = this.config;

        // Update mouse tracking
        this.updateMouseActivity(timestamp);

        // Scale factor based on mouse speed
        const rawActivityFactor = Math.min(1, this.mouse.speed / 10);
        const activityFactor = Math.max(minActivityFactor, rawActivityFactor);

        // Calculate mouse speed factor for border effect
        const speedThreshold = 0.5;
        const mouseBorderFactor = this.mouse.speed > speedThreshold ?
            Math.min(1, (this.mouse.speed - speedThreshold) / 8) : 0;

        // Only process visible hexagons
        this.visibleHexagons.forEach(hexagon => {
            const dist = this.distance(this.mouse.x, this.mouse.y, hexagon.x, hexagon.y);

            // Skip detailed processing for distant hexagons
            const maxEffectRange = hexSize * Math.max(proximityThresholds[2], borderGradientRange);
            if (dist > maxEffectRange + 50) {
                // Distant hexagons fade to base values
                hexagon.currentOpacity += (baseOpacity - hexagon.currentOpacity) * slowFadeSpeed;
                hexagon.borderOpacity = borderBaseOpacity;
                hexagon.borderGradientStrength = 0;
                return;
            }

            // Determine fill opacity based on distance thresholds
            let newTargetOpacity;
            let transitionSpeed;

            if (dist < hexSize * proximityThresholds[0]) {
                newTargetOpacity = hoverOpacity * activityFactor;
                transitionSpeed = activeTransitionSpeed;
            } else if (dist < hexSize * proximityThresholds[1]) {
                newTargetOpacity = nearOpacity * activityFactor;
                transitionSpeed = activeTransitionSpeed;
            } else if (dist < hexSize * proximityThresholds[2]) {
                newTargetOpacity = farOpacity * activityFactor;
                transitionSpeed = activeTransitionSpeed;
            } else {
                newTargetOpacity = baseOpacity;
                transitionSpeed = slowFadeSpeed;
            }

            // Update fill opacity with smooth transition
            hexagon.targetOpacity = newTargetOpacity;
            hexagon.currentOpacity += (hexagon.targetOpacity - hexagon.currentOpacity) * transitionSpeed;

            // Border effect based on distance and mouse movement
            if (dist < hexSize * borderGradientRange && mouseBorderFactor > 0) {
                // Linear falloff based on distance
                const distanceFactor = 1 - (dist / (hexSize * borderGradientRange));

                // Combine distance factor with mouse movement factor
                const combinedFactor = distanceFactor * mouseBorderFactor;

                // Update border properties
                hexagon.borderOpacity = borderBaseOpacity + (borderMaxOpacity - borderBaseOpacity) * combinedFactor;
                hexagon.borderGradientStrength = combinedFactor;

                // Update angle for precise gradient direction
                hexagon.borderGradientAngle = this.angle(hexagon.x, hexagon.y, this.mouse.x, this.mouse.y);
            } else {
                // Reset to base values when out of range or no mouse movement
                hexagon.borderOpacity = borderBaseOpacity;
                hexagon.borderGradientStrength = 0;
            }
        });
    }

    // Update the intro animation
    updateIntroAnimation(timestamp) {
        if (!this.animationState.introActive) return;

        // Initialize start time if needed
        if (!this.animationState.introStartTime) {
            this.animationState.introStartTime = timestamp;
        }

        const { introFlashDuration } = this.config;
        const elapsed = timestamp - this.animationState.introStartTime;

        // End the animation if all hexagons have completed flashing
        let allComplete = true;
        for (const hex of this.animationState.flashingHexagons) {
            const hexElapsed = elapsed - hex.flashDelay;
            if (hexElapsed < introFlashDuration) {
                allComplete = false;
                break;
            }
        }

        if (allComplete) {
            this.animationState.introActive = false;
            this.animationState.flashingHexagons.forEach(hex => {
                hex.flashActive = false;
            });
            this.animationState.flashingHexagons = [];
        }
    }

    drawHexagons() {
        // Fill the entire canvas with the background color
        this.ctx.fillStyle = this.config.backgroundColor;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Only draw active visible hexagons
        this.visibleHexagons.forEach(hexagon => {
            this.drawHexagon(hexagon);
        });
    }

    animate(timestamp) {
        // Skip rendering if image not loaded yet
        if (!this.imageLoaded) {
            requestAnimationFrame(this.animate);
            return;
        }

        // Update intro animation
        this.updateIntroAnimation(timestamp);

        // Throttled opacity updates
        this.updateOpacities(timestamp);

        // Update visible hexagons
        if (timestamp % 10 < 1 || this.visibleHexagons.length === 0) {
            this.updateVisibleHexagons();
        }

        this.drawHexagons();
        requestAnimationFrame(this.animate);
    }

    handleMouseMove(e) {
        // Throttle mouse move events
        const now = performance.now();
        if (now - this.mouse.lastMoveTime < this.config.throttleTime / 2) {
            return;
        }

        const canvasRect = this.canvas.getBoundingClientRect();

        this.mouse.x = e.clientX - canvasRect.left;
        this.mouse.y = e.clientY - canvasRect.top;
        this.mouse.lastMoveTime = now;
    }

    handleResize() {
        this.resizeCanvas();
    }

    setupEventListeners() {
        window.addEventListener('mousemove', this.handleMouseMove.bind(this));
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    destroy() {
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('resize', this.handleResize);
    }
}